import { COMMITMENT_STATE_LABELS, PRIORITY_CLASS_LABELS, type Commitment, type CommitmentState, type LatencyStats, type LiveTransferItem, type LiveTransferState, type LiveTransferView, type PriorityClass, type PriorityQueueItem } from '@lia/contracts';
import { formatDateTime, formatDuration } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';

// Setter §06 priority engine: the call queue ordered by explicit classes, committed-time tasks (committed vs actual)
// and latency stats. Unknown ≠ 0: no samples read «Sin datos»; an attempt that did not happen is never shown as done.

export const PRIORITY_CLASS_TONE: Record<PriorityClass, StatusTone> = {
  inbound_live: 'danger',
  committed_time: 'accent',
  team_request: 'info',
  imminent_reminder: 'warning',
  no_show_recovery: 'warning',
  new_lead: 'success',
  night_lead: 'info',
  qualified_no_appointment: 'neutral',
  window_expiring: 'neutral',
  routine_cadence: 'neutral',
};

export function PriorityClassBadge({ cls, className }: { cls: PriorityClass; className?: string }) {
  return (
    <StatusBadge tone={PRIORITY_CLASS_TONE[cls]} className={className}>
      {PRIORITY_CLASS_LABELS[cls]}
    </StatusBadge>
  );
}

const COMMITMENT_TONE: Record<CommitmentState, StatusTone> = { scheduled: 'info', queued: 'warning', attempted: 'success', sent: 'success', cancelled: 'neutral', blocked: 'danger', failed: 'danger' };

export function CommitmentStateBadge({ state }: { state: CommitmentState }) {
  return <StatusBadge tone={COMMITMENT_TONE[state]}>{COMMITMENT_STATE_LABELS[state]}</StatusBadge>;
}

export interface PriorityQueueTableProps {
  items: readonly PriorityQueueItem[];
  timeZone: string;
  emptyText?: string;
  /** Show the position column (waiting items). */
  showPosition?: boolean;
  caption?: string;
}

/** Calls waiting for (or granted) AI call capacity, highest priority first. */
export function PriorityQueueTable({ items, timeZone, emptyText = 'No hay llamadas en espera de capacidad.', showPosition = true, caption = 'Llamadas en cola' }: PriorityQueueTableProps) {
  if (!items.length) return <p className="text-body text-text-secondary">{emptyText}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-body">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="text-left text-meta text-text-secondary">
            {showPosition ? <th scope="col" className="py-1 pr-2 font-medium">#</th> : null}
            <th scope="col" className="py-1 pr-2 font-medium">Prospecto</th>
            <th scope="col" className="py-1 pr-2 font-medium">Prioridad</th>
            <th scope="col" className="py-1 pr-2 font-medium">Lista desde</th>
            <th scope="col" className="py-1 pr-2 text-right font-medium">Espera</th>
            <th scope="col" className="py-1 font-medium">Detalle</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-t border-border-subtle align-top">
              {showPosition ? <td className="py-1 pr-2 text-text-secondary">{i.position ?? '—'}</td> : null}
              <th scope="row" className="py-1 pr-2 text-left font-normal text-text-primary">
                {i.contactName ?? 'Sin nombre'}
                {i.projectName ? <span className="block text-meta text-text-secondary">{i.projectName}</span> : null}
              </th>
              <td className="py-1 pr-2">
                <PriorityClassBadge cls={i.class} />
                {i.committedAt ? <span className="block text-meta text-text-secondary">Pactada: {formatDateTime(i.committedAt, timeZone)}</span> : null}
              </td>
              <td className="py-1 pr-2 text-text-secondary">{formatDateTime(i.readyAt, timeZone)}</td>
              <td className="py-1 pr-2 text-right text-text-primary">{formatDuration(i.waitedSeconds)}</td>
              <td className="py-1 text-meta text-text-secondary">{i.state === 'admitted' ? 'Capacidad asignada' : (i.lastReason ?? 'En espera')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface CommitmentTableProps {
  commitments: readonly Commitment[];
  timeZone: string;
  /** Cancel action for scheduled commitments (managers). */
  onCancel?: (c: Commitment) => void;
}

/** Times agreed with prospects: committed vs actual attempt and the delay (§06.9). */
export function CommitmentTable({ commitments, timeZone, onCancel }: CommitmentTableProps) {
  if (!commitments.length) return <p className="text-body text-text-secondary">No hay horas pactadas en el periodo.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-body">
        <caption className="sr-only">Horas pactadas con prospectos</caption>
        <thead>
          <tr className="text-left text-meta text-text-secondary">
            <th scope="col" className="py-1 pr-2 font-medium">Prospecto</th>
            <th scope="col" className="py-1 pr-2 font-medium">Canal</th>
            <th scope="col" className="py-1 pr-2 font-medium">Hora pactada</th>
            <th scope="col" className="py-1 pr-2 font-medium">Intento real</th>
            <th scope="col" className="py-1 pr-2 text-right font-medium">Demora</th>
            <th scope="col" className="py-1 pr-2 font-medium">Estado</th>
            {onCancel ? <th scope="col" className="py-1 font-medium"><span className="sr-only">Acciones</span></th> : null}
          </tr>
        </thead>
        <tbody>
          {commitments.map((c) => (
            <tr key={c.id} className="border-t border-border-subtle align-top">
              <th scope="row" className="py-1 pr-2 text-left font-normal text-text-primary">
                {c.contactName ?? 'Sin nombre'}
                {c.note ? <span className="block text-meta text-text-secondary">{c.note}</span> : null}
              </th>
              <td className="py-1 pr-2 text-text-secondary">{c.channel === 'whatsapp' ? 'WhatsApp' : 'Llamada'}</td>
              <td className="py-1 pr-2 text-text-secondary">{formatDateTime(c.committedAt, timeZone)}</td>
              <td className="py-1 pr-2 text-text-secondary">{c.actualAt ? formatDateTime(c.actualAt, timeZone) : <span className="italic">Sin intento aún</span>}</td>
              <td className="py-1 pr-2 text-right text-text-primary">{c.delaySeconds === null ? '—' : formatDuration(c.delaySeconds)}</td>
              <td className="py-1 pr-2">
                <CommitmentStateBadge state={c.state} />
                {c.reason ? <span className="block text-meta text-text-secondary">{c.reason}</span> : null}
              </td>
              {onCancel ? (
                <td className="py-1">
                  {c.state === 'scheduled' || (c.channel === 'whatsapp' && c.state === 'queued' && !c.actualAt) ? (
                    <Button type="button" variant="link" size="sm" onClick={() => onCancel(c)} aria-label={`Cancelar ${c.channel === 'whatsapp' ? 'el WhatsApp pactado' : 'la llamada pactada'} con ${c.contactName ?? 'el prospecto'}`}>
                      Cancelar
                    </Button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** p50/p95 of a latency; no samples → «Sin datos» (never 0). */
export function LatencySummary({ label, stats, hint }: { label: string; stats: LatencyStats; hint?: string }) {
  return (
    <div className="rounded-card border border-border-subtle p-3">
      <p className="text-meta text-text-secondary">{label}</p>
      {stats.samples === 0 || stats.p50Seconds === null ? (
        <p className="text-body-lg font-semibold text-text-secondary">Sin datos</p>
      ) : (
        <>
          <p className="text-body-lg font-semibold text-text-primary">{`p50 ${formatDuration(stats.p50Seconds)} · p95 ${formatDuration(stats.p95Seconds)}`}</p>
          <p className="text-meta text-text-secondary">{`${stats.samples} ${stats.samples === 1 ? 'muestra' : 'muestras'}`}</p>
        </>
      )}
      {hint ? <p className="mt-1 text-meta text-text-secondary">{hint}</p> : null}
    </div>
  );
}

// ───────── Live AI→human transfer (setter §05.22 / §05.23, TECH-03) ─────────

const LIVE_TRANSFER_TONE: Record<LiveTransferState, StatusTone> = {
  requested: 'info',
  uncertain: 'warning',
  answered: 'success',
  not_answered: 'warning',
  failed: 'danger',
  no_target: 'neutral',
  unsupported: 'neutral',
};

export function LiveTransferStateBadge({ transfer }: { transfer: Pick<LiveTransferView, 'state' | 'label'> }) {
  return <StatusBadge tone={LIVE_TRANSFER_TONE[transfer.state]}>{transfer.label}</StatusBadge>;
}

/** One call's live transfer: what LIA asked, to whom, and whether the provider reported the advisor answered. */
export function LiveTransferSummary({ transfer, timeZone }: { transfer: LiveTransferView; timeZone: string }) {
  const open = transfer.state === 'requested' || transfer.state === 'uncertain';
  return (
    <div className="grid gap-1">
      <span>
        <LiveTransferStateBadge transfer={transfer} />
      </span>
      {transfer.targetName ? <span className="text-body text-text-primary">{`Asesor: ${transfer.targetName}`}</span> : null}
      <span className="text-meta text-text-secondary">{`Solicitada ${formatDateTime(transfer.requestedAt, timeZone)}`}</span>
      {transfer.answeredAt ? <span className="text-meta text-text-secondary">{`Contestó ${formatDateTime(transfer.answeredAt, timeZone)}`}</span> : null}
      {open ? <span className="text-meta text-text-secondary">Aún sin confirmación de que el asesor contestó: la tarea sigue con su responsable.</span> : null}
      {transfer.endedAt && transfer.state === 'answered' ? <span className="text-meta text-text-secondary">{`Terminó ${formatDateTime(transfer.endedAt, timeZone)}`}</span> : null}
      {transfer.detail ? <span className="text-meta text-text-secondary">{transfer.detail}</span> : null}
    </div>
  );
}

/** Recent live transfers on AI calls («Cola de llamadas»). */
export function LiveTransferTable({ transfers, timeZone }: { transfers: readonly LiveTransferItem[]; timeZone: string }) {
  if (!transfers.length) return <p className="text-body text-text-secondary">No hubo transferencias en vivo en el periodo.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-body">
        <caption className="sr-only">Transferencias en vivo a un asesor</caption>
        <thead>
          <tr className="text-left text-meta text-text-secondary">
            <th scope="col" className="py-1 pr-2 font-medium">Prospecto</th>
            <th scope="col" className="py-1 pr-2 font-medium">Asesor</th>
            <th scope="col" className="py-1 pr-2 font-medium">Solicitada</th>
            <th scope="col" className="py-1 pr-2 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((t) => (
            <tr key={`${t.callId}-${t.requestedAt}`} className="border-t border-border-subtle align-top">
              <th scope="row" className="py-1 pr-2 text-left font-normal text-text-primary">
                {t.contactName ?? 'Sin nombre'}
                {t.projectName ? <span className="block text-meta text-text-secondary">{t.projectName}</span> : null}
              </th>
              <td className="py-1 pr-2 text-text-primary">{t.targetName ?? '—'}</td>
              <td className="py-1 pr-2 text-text-secondary">{formatDateTime(t.requestedAt, timeZone)}</td>
              <td className="py-1 pr-2">
                <LiveTransferStateBadge transfer={t} />
                {t.detail ? <span className="block text-meta text-text-secondary">{t.detail}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
